import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Alert, Button, Empty, Modal, Spin, Table, Tag, Tooltip, message } from "antd";
import {
  CheckCircleTwoTone,
  CloseCircleFilled,
  DownloadOutlined,
  ExclamationCircleFilled,
  FilePdfOutlined,
  ReloadOutlined,
  TableOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import { presentationsApi, templatesApi } from "../../../api/presentationsApi";
import {
  CoverageState,
  IChecklistStep,
  IListingMaterials,
  IMaterialRow,
  IPresentationTemplate,
  ITemplateRow,
  NAMESPACE_LABELS,
  STATE_COLORS,
  STATE_LABELS,
} from "../../../types/models/presentations";

// Heavy (pdf.js, every slide editor): only loaded when someone opens it.
const PrepareModal = lazy(() => import("./PrepareModal"));

/**
 * The sales-materials panel for one listing — the daily surface, opened from
 * the listing wizard's last step.
 *
 * Three things it must communicate:
 *
 * 1. **Nothing is copied.** The data is injected fresh on every generation,
 *    and the panel says so out loud, because otherwise nobody believes it.
 *
 * 2. **What is still needed, before anyone clicks.** It reads the same
 *    checklist the template drawer gates on (`readiness.steps`): a `blocking`
 *    step means the server will refuse, so Generate is disabled and the step
 *    has a button to fix it; a `warning` generates anyway, a little worse (a
 *    slide left out, a blank detail), and says so.
 *
 * 3. **Missing data is not an error.** A material that cannot be generated yet
 *    is "Needs info", in amber. Red "Failed" is kept for a render that actually
 *    broke — the thing worth reporting — and its reason is shown on its row.
 */

const Container = styled.div`
  .live-note {
    display: flex;
    align-items: center;
    gap: 8px;
    color: rgba(0, 0, 0, 0.45);
    font-size: 13px;
    margin-bottom: 16px;
  }

  .needs {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .needs li {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px dashed rgba(0, 0, 0, 0.08);
  }

  .needs li:last-child {
    border-bottom: 0;
  }

  .needs .text {
    flex: 1;
    min-width: 0;
  }

  .needs .detail {
    display: block;
    color: rgba(0, 0, 0, 0.55);
    font-size: 12px;
  }

  .needs .applies {
    margin-left: 6px;
  }

  .group-title {
    font-weight: 500;
    margin: 4px 0 2px;
  }

  .reason {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }

  .row-error {
    color: #cf1322;
    font-size: 12px;
    margin-top: 4px;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .row-note {
    color: #ad6800;
    font-size: 12px;
    margin-top: 4px;
  }
`;

const POLL_MS = 3000;

interface Props {
  listingId: number;
  /** Rendered inside the listing wizard, which is already narrow. */
  compact?: boolean;
  /**
   * Take the person to where the property's own details are edited. In the
   * listing wizard that is its Details step; without it, those steps get no
   * button and say where to go instead.
   */
  onEditDetails?: () => void;
}

const materialKey = (row: IMaterialRow) => `${row.namespace}-${row.language}`;

const materialLabel = (row: IMaterialRow) =>
  `${NAMESPACE_LABELS[row.namespace] ?? row.namespace} ${row.language.toUpperCase()}`;

/** Whether the server would refuse to generate this material right now. */
const isBlocked = (row: IMaterialRow) => Boolean(row.checklist?.blocked);

/** One open checklist step, merged across the materials it applies to. */
interface INeed {
  step: IChecklistStep;
  /** The materials with this exact step open; the first is opened to fix it. */
  rows: IMaterialRow[];
}

/**
 * The open steps of every material, merged. ES and EN draw from the same
 * listing, so a missing floor plan is one thing to fix, not two — but a
 * caption missing in ES only is a different step from one missing in EN, and
 * the detail text says so, so merging on kind *and* detail keeps both.
 */
function collectNeeds(rows: IMaterialRow[]) {
  const needs = new Map<string, INeed>();
  rows.forEach((row) => {
    row.checklist?.steps
      .filter((step) => step.status !== "done")
      .forEach((step) => {
        const key = `${step.status}|${step.kind}|${step.detail ?? ""}`;
        const need = needs.get(key);
        if (need) need.rows.push(row);
        else needs.set(key, { step, rows: [row] });
      });
  });
  const all = Array.from(needs.values());
  return {
    blocking: all.filter((need) => need.step.status === "blocking"),
    warnings: all.filter((need) => need.step.status === "warning"),
  };
}

function SalesMaterialsPanel({ listingId, compact, onEditDetails }: Props) {
  const [data, setData] = useState<IListingMaterials | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [payload, setPayload] = useState<Record<string, any> | null>(null);
  // The material the Prepare screen is open for, and what it needs to open.
  const [prepare, setPrepare] = useState<{
    key: string;
    template: IPresentationTemplate;
    listing: ITemplateRow["listing"] | null;
  } | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const pollRef = useRef<any>(null);

  const load = useCallback(async () => {
    try {
      setData(await presentationsApi.forListing(listingId));
    } catch (e) {
      message.error("Could not load sales materials.");
    } finally {
      setLoading(false);
    }
  }, [listingId]);

  useEffect(() => {
    load();
    return () => clearInterval(pollRef.current);
  }, [load]);

  // Generation runs on the task cluster, so the panel polls until nothing is
  // still in flight rather than blocking on the request.
  useEffect(() => {
    const generating = data?.materials.some(
      (row) => row.presentation?.status === "pending"
    );
    clearInterval(pollRef.current);
    if (generating) {
      pollRef.current = setInterval(load, POLL_MS);
    }
    return () => clearInterval(pollRef.current);
  }, [data, load]);

  const generate = async (row: IMaterialRow) => {
    const key = materialKey(row);
    setBusy(key);
    try {
      await presentationsApi.generate(listingId, row.namespace, row.language);
      message.success("Generating — this takes about a minute.");
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      // 409: something became blocking since the panel loaded. Not an error —
      // reloading shows what is needed and disables the button.
      if (e?.response?.status === 409) message.warning(detail ?? "Not ready to generate yet.");
      else message.error(detail ?? "Could not start generation.");
    } finally {
      setBusy(null);
      await load();
    }
  };

  /** Open the template drawer's Prepare screen for one material. */
  const openPrepare = async (row: IMaterialRow) => {
    if (!row.template) return;
    const key = materialKey(row);
    setOpening(key);
    try {
      const detail = await templatesApi.detail(row.template.id);
      const own = detail.rows.find((r) => r.listing.id === listingId);
      setPrepare({ key, template: detail.template, listing: own?.listing ?? null });
    } catch (e) {
      message.error("Could not open this material's slides.");
    } finally {
      setOpening(null);
    }
  };

  const closePrepare = () => {
    setPrepare(null);
    load();
  };

  const showPayload = async (language: string) => {
    try {
      setPayload(await presentationsApi.payload(listingId, language));
    } catch (e) {
      message.error("Could not load the data preview.");
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Spin />
      </div>
    );
  }

  const rows = data?.materials ?? [];
  const withTemplates = rows.filter((row) => row.template);
  const { blocking, warnings } = collectNeeds(withTemplates);

  /** The button that fixes a step, or nothing when only the template can. */
  const fixButton = (need: INeed) => {
    const { step } = need;
    if (!step.action) return null;
    if (step.action === "listing") {
      return onEditDetails ? (
        <Button size="small" onClick={onEditDetails}>Go to Details</Button>
      ) : null;
    }
    const row = need.rows[0];
    return (
      <Button
        size="small"
        type={step.status === "blocking" ? "primary" : "default"}
        ghost={step.status === "blocking"}
        loading={opening === materialKey(row)}
        onClick={() => openPrepare(row)}
      >
        Fix
      </Button>
    );
  };

  const needList = (needs: INeed[]) => (
    <ul className="needs">
      {needs.map((need) => (
        <li key={`${need.step.kind}-${need.step.detail}-${need.rows.map(materialKey).join()}`}>
          {need.step.status === "blocking" ? (
            <CloseCircleFilled style={{ color: "#ff4d4f" }} />
          ) : (
            <ExclamationCircleFilled style={{ color: "#faad14" }} />
          )}
          <span className="text">
            <b>{need.step.label}</b>
            {/* Only worth saying which materials when it is not all of them. */}
            {need.rows.length < withTemplates.length &&
              need.rows.map((row) => (
                <Tag key={materialKey(row)} className="applies">{materialLabel(row)}</Tag>
              ))}
            {need.step.detail && <span className="detail">{need.step.detail}</span>}
          </span>
          {fixButton(need)}
        </li>
      ))}
    </ul>
  );

  const needsSummary = () => {
    if (withTemplates.length === 0) return null;
    if (blocking.length === 0 && warnings.length === 0) {
      return (
        <Alert
          type="success"
          showIcon
          icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
          message="Everything these materials need is filled in"
          style={{ marginBottom: 16 }}
        />
      );
    }
    return (
      <Alert
        type={blocking.length ? "warning" : "info"}
        showIcon
        style={{ marginBottom: 16 }}
        message={
          blocking.length
            ? `Before generating, ${blocking.length === 1 ? "one thing is" : `${blocking.length} things are`} still needed`
            : "Ready to generate — a few things will be left out"
        }
        description={
          <>
            {blocking.length > 0 && needList(blocking)}
            {warnings.length > 0 && (
              <>
                {blocking.length > 0 && (
                  <div className="group-title" style={{ marginTop: 8 }}>
                    Optional — generates without these
                  </div>
                )}
                {needList(warnings)}
              </>
            )}
          </>
        }
      />
    );
  };

  const columns = [
    {
      title: "Material",
      key: "material",
      render: (_: any, row: IMaterialRow) => (
        <div>
          <div>
            <b>{NAMESPACE_LABELS[row.namespace] ?? row.namespace}</b>{" "}
            <Tag style={{ marginLeft: 4 }}>{row.language.toUpperCase()}</Tag>
          </div>
          <span className="reason">
            {row.template
              ? `${row.template.name} — ${row.template_reason}`
              : row.template_reason}
          </span>
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 170,
      render: (_: any, row: IMaterialRow) => {
        if (!row.template) return <Tag>No template</Tag>;
        const presentation = row.presentation;
        // Read from the live checklist, not only the row: an old refusal whose
        // data has since been filled in is no longer "Needs info", and a
        // material never generated but blocked is. A row with a brochure
        // otherwise keeps its own state — the file is still good to download.
        const blocked = isBlocked(row);
        const hasOutput = Boolean(presentation?.pptx_url);
        let state: CoverageState = presentation?.status ?? "not_generated";
        if (state === "needs_info" && !blocked) {
          state = hasOutput ? "stale" : "not_generated";
        } else if (state === "not_generated" && blocked) {
          state = "needs_info";
        }
        return (
          <>
            <Tag color={STATE_COLORS[state]}>{STATE_LABELS[state]}</Tag>
            {presentation?.generated_at && (
              <div className="reason">
                {dayjs(presentation.generated_at).format("D MMM")}
              </div>
            )}
            {/* A real failure says why, here, not in a box below the fold. */}
            {presentation?.status === "failed" && presentation.error && (
              <Tooltip title={presentation.error}>
                <div className="row-error">{presentation.error}</div>
              </Tooltip>
            )}
            {/* A READY row's error is a PDF that did not convert. */}
            {presentation?.status !== "failed" && presentation?.status !== "needs_info"
              && presentation?.error && (
              <Tooltip title={presentation.error}>
                <div className="row-note">PDF not available</div>
              </Tooltip>
            )}
          </>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 280,
      align: "right" as const,
      render: (_: any, row: IMaterialRow) => {
        if (!row.template) return null;
        const key = materialKey(row);
        const presentation = row.presentation;
        const generating = presentation?.status === "pending";
        const blocked = isBlocked(row);
        const hasOutput = Boolean(presentation?.pptx_url);
        return (
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            {presentation?.pdf_url && (
              <Tooltip title="Download PDF">
                <Button
                  size="small"
                  icon={<FilePdfOutlined />}
                  href={presentation.pdf_url}
                  target="_blank"
                />
              </Tooltip>
            )}
            {presentation?.pptx_url && (
              <Tooltip title="Download PowerPoint">
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  href={presentation.pptx_url}
                  target="_blank"
                />
              </Tooltip>
            )}
            {presentation?.teaser?.pdf_url && (
              <Tooltip title="The teaser: this brochure with slides left out">
                <Button size="small" href={presentation.teaser.pdf_url} target="_blank">
                  Teaser
                </Button>
              </Tooltip>
            )}
            {blocked && (
              <Button
                size="small"
                type="primary"
                ghost
                loading={opening === key}
                onClick={() => openPrepare(row)}
              >
                Fix what’s missing
              </Button>
            )}
            <Tooltip
              title={blocked ? "Fill in what is listed above first." : ""}
            >
              <Button
                size="small"
                type={!blocked && !hasOutput ? "primary" : "default"}
                disabled={blocked}
                loading={busy === key || generating}
                icon={hasOutput ? <ReloadOutlined /> : undefined}
                onClick={() => generate(row)}
              >
                {hasOutput ? "Regenerate" : "Generate"}
              </Button>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const prepareRow = (() => {
    if (!prepare || !data) return null;
    const row = rows.find((r) => materialKey(r) === prepare.key);
    if (!row) return null;
    // Kept current from the panel's own polling, so a generation started from
    // the Prepare screen shows there as it runs.
    const listing: ITemplateRow["listing"] = prepare.listing ?? {
      ...data.listing,
      has_floor_plan: !row.checklist?.steps.some(
        (step) => step.kind === "floor_plan" && step.status !== "done"
      ),
    };
    return {
      material: row,
      row: {
        listing,
        presentation: row.presentation,
        copy: { blocking: 0, drafts: 0 },
        in_audience: true,
      } as ITemplateRow,
    };
  })();

  return (
    <Container>
      <div className="live-note">
        <CheckCircleTwoTone twoToneColor="#52c41a" />
        Always uses this listing’s saved data. Nothing is copied — regenerating
        picks up every edit you have saved since.
      </div>

      {needsSummary()}

      {rows.length === 0 ? (
        <Empty description="No sales materials configured" />
      ) : (
        <Table
          rowKey={materialKey}
          dataSource={rows}
          columns={columns}
          pagination={false}
          size={compact ? "small" : "middle"}
        />
      )}

      <Button
        type="link"
        size="small"
        icon={<TableOutlined />}
        style={{ paddingLeft: 0, marginTop: 8 }}
        onClick={() => showPayload("es")}
      >
        See the data that will be used
      </Button>

      <Modal
        visible={!!payload}
        onCancel={() => setPayload(null)}
        footer={null}
        width={760}
        title="Data that will be injected"
      >
        <p style={{ color: "rgba(0,0,0,.45)" }}>
          Blank values render as empty space on the slide. Fix them on the listing,
          not on the brochure.
        </p>
        <Table
          size="small"
          pagination={false}
          scroll={{ y: 420 }}
          rowKey="path"
          columns={[
            { title: "Field", dataIndex: "path", width: 320 },
            {
              title: "Value",
              dataIndex: "value",
              render: (value: string) =>
                value ? (
                  value
                ) : (
                  <Tag color="warning">empty</Tag>
                ),
            },
          ]}
          dataSource={Object.entries(payload?.tokens ?? {}).map(
            ([path, value]) => ({ path, value: value as string })
          )}
        />
      </Modal>

      {prepare && prepareRow && (
        <Suspense fallback={null}>
          <PrepareModal
            template={prepare.template}
            row={prepareRow.row}
            open
            onClose={closePrepare}
            onGenerate={() => generate(prepareRow.material)}
            onEditListing={() => {
              closePrepare();
              onEditDetails?.();
            }}
            onChanged={load}
          />
        </Suspense>
      )}
    </Container>
  );
}

export default SalesMaterialsPanel;
